import { SPECIAL_REQUESTS_MAX_LENGTH } from '../lib/bookingNotes'
import { sanitizeRegistration } from '../lib/format'

export interface ContactDetails {
  name: string
  email: string
  phone: string
}

export interface DetailsStepProps {
  /**
   * Set when the booking still needs a registration — the tyre journey lets
   * customers price by size alone, but a booking cannot be created without a
   * vehicle.
   */
  registration?: {
    value: string
    onChange: (value: string) => void
    error?: string | null
  } | null
  details: ContactDetails
  onDetailsChange: (details: ContactDetails) => void
  specialRequests: string
  onSpecialRequestsChange: (value: string) => void
  fieldErrors: Partial<Record<keyof ContactDetails, string>>
}

export function DetailsStep({
  registration,
  details,
  onDetailsChange,
  specialRequests,
  onSpecialRequestsChange,
  fieldErrors,
}: DetailsStepProps) {
  const update = (patch: Partial<ContactDetails>) => onDetailsChange({ ...details, ...patch })

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">Almost done</p>
        <h3 className="ac-heading">Where do we send the confirmation?</h3>
      </div>

      {registration ? (
        <div className="ac-field">
          <label className="ac-label" htmlFor="ac-details-reg">
            Vehicle registration
          </label>
          <div className="ac-plate">
            <span className="ac-plate__country" aria-hidden="true">
              <span className="ac-plate__stars">★</span>
              GB
            </span>
            <input
              id="ac-details-reg"
              type="text"
              className="ac-plate__input"
              placeholder="AB12 CDE"
              value={registration.value}
              onChange={(event) => registration.onChange(sanitizeRegistration(event.target.value))}
              maxLength={8}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={Boolean(registration.error)}
              aria-describedby={registration.error ? 'ac-details-reg-error' : undefined}
            />
          </div>
          {registration.error ? (
            <p className="ac-field-error" id="ac-details-reg-error">
              {registration.error}
            </p>
          ) : (
            <p className="ac-hint">So we know which vehicle is coming in.</p>
          )}
        </div>
      ) : null}

      <div className="ac-field">
        <label className="ac-label" htmlFor="ac-name">
          Full name
        </label>
        <input
          id="ac-name"
          className="ac-input"
          value={details.name}
          onChange={(event) => update({ name: event.target.value })}
          autoComplete="name"
          required
          aria-invalid={Boolean(fieldErrors.name)}
          aria-describedby={fieldErrors.name ? 'ac-name-error' : undefined}
        />
        {fieldErrors.name ? (
          <p className="ac-field-error" id="ac-name-error">
            {fieldErrors.name}
          </p>
        ) : null}
      </div>

      <div className="ac-field">
        <label className="ac-label" htmlFor="ac-email">
          Email
        </label>
        <input
          id="ac-email"
          type="email"
          className="ac-input"
          value={details.email}
          onChange={(event) => update({ email: event.target.value })}
          autoComplete="email"
          inputMode="email"
          required
          aria-invalid={Boolean(fieldErrors.email)}
          aria-describedby={fieldErrors.email ? 'ac-email-error' : undefined}
        />
        {fieldErrors.email ? (
          <p className="ac-field-error" id="ac-email-error">
            {fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div className="ac-field">
        <label className="ac-label" htmlFor="ac-phone">
          Phone
        </label>
        <input
          id="ac-phone"
          type="tel"
          className="ac-input"
          value={details.phone}
          onChange={(event) => update({ phone: event.target.value })}
          autoComplete="tel"
          inputMode="tel"
          placeholder="07700 900000"
          required
          aria-invalid={Boolean(fieldErrors.phone)}
          aria-describedby={fieldErrors.phone ? 'ac-phone-error' : undefined}
        />
        {fieldErrors.phone ? (
          <p className="ac-field-error" id="ac-phone-error">
            {fieldErrors.phone}
          </p>
        ) : null}
      </div>

      <div className="ac-field">
        <label className="ac-label" htmlFor="ac-notes">
          Anything we should know? (optional)
        </label>
        <textarea
          id="ac-notes"
          className="ac-input"
          rows={3}
          value={specialRequests}
          maxLength={SPECIAL_REQUESTS_MAX_LENGTH}
          onChange={(event) => onSpecialRequestsChange(event.target.value)}
          placeholder="Warning lights, noises, or anything else"
        />
      </div>
    </div>
  )
}
